import { DateTime, Interval } from 'luxon';
import pluralize from 'pluralize';
import { Container, CurrentContainerStatus, HoldState } from 'types/Watchlist';
import { DATE_FORMATS } from './date-formats';
import { centsToDollars, toUppercase } from './helpers';

export interface FormattedValue<T = void> {
  /** The human readable value to display */
  value: string;
  /** Additional data to include */
  meta?: T;
}

/**
 * Properly format a given hold
 * @param status
 * @param note
 * @returns
 */
export const formatHoldStatus = (
  status: string | null,
  note: string | null
): FormattedValue<{ indicate: boolean }> | null => {
  const indicate = status === HoldState.Hold || (status === HoldState.Unknown && !!note);
  const raw = status === HoldState.Unknown ? note : status;

  if (!raw) {
    return null;
  }

  const value = toUppercase(raw);

  return { value, meta: { indicate } };
};

interface ContainerStatusPillFormat {
  label: string;
  colors: { background: string; text: string };
  tooltip?: string;
}

const CONTAINER_STATUS_FORMATS: Record<CurrentContainerStatus | 'all', ContainerStatusPillFormat> =
  {
    all: {
      label: 'All active shipments',
      colors: { background: 'bg-blue-300', text: 'text-blue-900' },
    },
    [CurrentContainerStatus.GatedOut]: {
      label: 'Gated out',
      colors: { background: 'bg-gatedOut-100', text: 'text-gatedOut-900' },
    },
    [CurrentContainerStatus.Terminated]: {
      label: 'Terminated',
      colors: { background: 'bg-gray-200', text: 'text-gray-600' },
    },
    [CurrentContainerStatus.OnVessel]: {
      label: 'On vessel',
      colors: { background: 'bg-vessel-100', text: 'text-vessel-900' },
    },
    [CurrentContainerStatus.Manifested]: {
      label: 'Manifested',
      colors: { background: 'bg-green-100', text: 'text-green-900' },
    },
    [CurrentContainerStatus.AtTerminal]: {
      label: 'At terminal',
      colors: { background: 'bg-terminal-100', text: 'text-terminal-900' },
    },
    [CurrentContainerStatus.Unknown]: {
      label: 'Unknown',
      colors: { background: 'bg-gray-200', text: 'text-gray-600' },
    },
    [CurrentContainerStatus.OnRail]: {
      label: 'On rail',
      colors: { background: 'bg-onRail-100', text: 'text-onRail-900' },
    },
    [CurrentContainerStatus.OffRail]: {
      label: 'Off rail',
      colors: { background: 'bg-offRail-100', text: 'text-offRail-900' },
    },
    [CurrentContainerStatus.GateOutFromRailFacility]: {
      label: 'Gate out from rail facility',
      colors: { background: 'bg-gatedOutFromRail-100', text: 'text-gatedOutFromRail-900' },
    },
  };

export const getContainerStatusFormat = (status: CurrentContainerStatus | 'all') => {
  return CONTAINER_STATUS_FORMATS[status] || CONTAINER_STATUS_FORMATS.unknown;
};

export const formatDestinationLocationPort = (container: Container): FormattedValue | null => {
  const { pickupLocation } = container;

  const value = pickupLocation?.port?.name || pickupLocation?.portRaw;
  if (!value) {
    return null;
  }

  return { value };
};

export const formatPickupLocationPort = (container: Container): FormattedValue | null => {
  return formatDestinationLocationPort(container);
};

export const formatPickupLocationTerminal = (container: Container): FormattedValue | null => {
  const { pickupLocation } = container;

  const terminalName = pickupLocation?.terminal?.name || pickupLocation?.terminalRaw;

  const pier = toUppercase(pickupLocation?.terminal?.pier || '', true);

  let value = terminalName;
  if (!value) {
    return null;
  }

  if (pier !== toUppercase(value, true)) {
    value = `${value} ${pier}`;
  }

  return { value };
};

export const formatFinalDestination = (container: Container): FormattedValue | null => {
  const { shipment } = container;
  if (shipment?.finalDestinationRaw) {
    return { value: shipment.finalDestinationRaw };
  }
  return null;
};

export const formatDemurrage = (
  container: Container
): FormattedValue<{
  demurrageDue: boolean;
  numberOfDays: number | null;
  displayAlert: boolean;
}> | null => {
  const demurragePeriod = container.pickupInfo?.demurragePeriod;
  if (demurragePeriod) {
    const { demurrageDue, numberOfDays, displayAlert, demurrageFeeUsCents } = demurragePeriod;
    if (demurrageFeeUsCents !== null && demurrageDue) {
      const value = centsToDollars(demurrageFeeUsCents);
      return { value, meta: { demurrageDue, numberOfDays, displayAlert } };
    }
  }
  return null;
};

/**
  Format the last free day of a container for display
  @param {Container} container - The container to format the last free day for.
  @returns {FormattedValue<{ delta?: number; late?: boolean }> | null} - The formatted last free day
  value and metadata, or null if no last free day is available.
  @description This function formats the last free day of a container by comparing it to the
  current date or gated out date if available.
  If the container is at the terminal, the function compares the last free day to the current time
  in the container's timezone. If the container has been gated out, the function compares the last
  free day to the gated out time in the container's timezone. Delta is the difference in days
  between the last free day and the current time or gated out time. If delta is greater than 0, the
  container is late.
*/
export const formatLastFreeDay = (
  container: Container
): FormattedValue<{ delta?: number; late?: boolean }> | null => {
  // Extract all the relevant data from the container
  const { pickupInfo, currentStatus, gatedOut } = container;
  const pickupLastFreeDay = pickupInfo?.pickupDates?.lastFreeDay;
  const timezone = pickupInfo?.timezone;

  // Check if the container is missing the last free day
  if (!pickupLastFreeDay) return null;

  // Parse and format the last free day
  const lastFreeDay = DateTime.fromFormat(pickupLastFreeDay, DATE_FORMATS.BE_DATE_FORMAT, {
    zone: timezone,
  });
  const value = lastFreeDay.toFormat(DATE_FORMATS.SINGLE_DATE_FORMAT);

  // Determine the date to compare against
  let compareTo: DateTime | null = null;

  if (currentStatus === CurrentContainerStatus.AtTerminal) {
    compareTo = DateTime.now().setZone(timezone);
  } else if (gatedOut?.occurred) {
    // If the container is not at terminal, then compare the LFD to the date the container was gated out
    compareTo = DateTime.fromFormat(gatedOut.occurred, DATE_FORMATS.BE_DATE_TIME, {
      zone: timezone,
    });
  }

  // If we have a date to compare against
  if (compareTo) {
    // Calculate the difference in days between the LFD and the date to compare against
    const delta = Math.floor(compareTo.diff(lastFreeDay, 'days').as('days'));
    // If the difference is greater than 0, the container is late
    const late = delta > 0 && container.currentStatus !== CurrentContainerStatus.GatedOut;

    return { value, meta: { delta, late } };
  }

  return { value };
};

/**
  Format the good thru date of a container for display
  @param {Container} container - The container to format the GTD for.
  @returns {FormattedValue<{ delta?: number; late?: boolean }> | null} - The formatted GTD value
  and metadata, or null if no GTD is available.
  @description This function formats the good thru date of a container by comparing it to the
  current date or gated out date if available.
  If the container is at the terminal, the function compares the good thru date to the current time
  in the container's timezone. If the container has been gated out, the function compares the GTD
  to the gated out time in the container's timezone. Delta is the difference in days between the
  GTD and the current time or gated out time. If delta is greater than 0, the container is late.
*/
export const formatGTD = (
  container: Container
): FormattedValue<{ delta?: number; late?: boolean }> | null => {
  // Get the container's pickup dates and GTD
  const { pickupInfo, currentStatus, gatedOut } = container;
  const pickupDates = pickupInfo?.pickupDates;
  const pickupGTD = pickupDates?.goodThroughDate;
  const timezone = pickupInfo?.timezone;

  // If there's no GTD, return null
  if (!pickupGTD) return null;

  // Create a DateTime object from the GTD and the container's timezone
  const GTD = DateTime.fromFormat(pickupGTD, DATE_FORMATS.BE_DATE_FORMAT, {
    zone: timezone,
  });
  const value = GTD.toFormat(DATE_FORMATS.SINGLE_DATE_FORMAT);

  let compareTo: DateTime | null = null;

  if (currentStatus === CurrentContainerStatus.AtTerminal) {
    compareTo = DateTime.now().setZone(timezone);
  } else if (gatedOut?.occurred) {
    // If the container is not at terminal, then compare the GTD to the date the container was gated out
    compareTo = DateTime.fromFormat(gatedOut.occurred, DATE_FORMATS.BE_DATE_TIME, {
      zone: timezone,
    });
  }

  // If we have a date to compare against
  if (compareTo) {
    // Calculate the difference in days between the GTD and the date to compare against
    const delta = Math.floor(compareTo.diff(GTD, 'days').as('days'));
    // If the difference is greater than 0, the container is late
    const late = delta > 0 && container.currentStatus !== CurrentContainerStatus.GatedOut;

    return { value, meta: { delta, late } };
  }

  return { value };
};

export const formatOriginalLastFreeDay = (container: Container): FormattedValue | null => {
  const { pickupInfo } = container;
  const pickupDates = pickupInfo?.pickupDates;

  if (pickupDates && pickupDates.originalLastFreeDay) {
    const originalLastFreeDay = DateTime.fromISO(pickupDates.originalLastFreeDay);
    return { value: originalLastFreeDay.toFormat(DATE_FORMATS.SINGLE_DATE_FORMAT) };
  }
  return null;
};

export const formatScheduledAppointment = (container: Container): FormattedValue | null => {
  const { pickupInfo } = container;
  const pickupAppointment = pickupInfo?.pickupAppointment;

  if (pickupAppointment && pickupAppointment.startTime) {
    const start = DateTime.fromISO(pickupAppointment.startTime);
    const end = pickupAppointment.endTime ? DateTime.fromISO(pickupAppointment.endTime) : undefined;

    if (start && end) {
      const interval = Interval.fromDateTimes(start, end);
      return {
        value: `${start.toFormat('MM/dd/yyyy')} ${interval.toFormat('hh:mm')} ${start.toFormat(
          'ZZZZ'
        )}`,
      };
    }
    if (start) {
      return { value: start.toFormat('MM/dd/yyyy hh:mm ZZZZ') };
    }
  }

  return null;
};

export const formatGatedOut = (container: Container): FormattedValue | null => {
  if (container.gatedOut) {
    const date = DateTime.fromFormat(container.gatedOut.occurred, DATE_FORMATS.BE_DATE_TIME, {
      zone: container.gatedOut.timezone,
    });
    return { value: date.toISO() };
  }
  return null;
};

export const formatGatedIn = (container: Container): FormattedValue | null => {
  if (container.gatedIn) {
    const date = DateTime.fromFormat(container.gatedIn.occurred, DATE_FORMATS.BE_DATE_TIME, {
      zone: container.gatedIn.timezone,
    });
    return { value: date.toISO() };
  }
  return null;
};

export const formatReturnLocation = (
  container: Container
): FormattedValue<{ terminal: string | null }> | null => {
  if (container.gatedIn) {
    const value = container.gatedIn.portName;
    if (value) {
      return { value, meta: { terminal: container.gatedIn.terminalName } };
    }
  }
  return null;
};

const DETENTION_ALERT_DAYS_OUT = 5;
export const formatDetentionPeriod = (
  container: Container
): FormattedValue<{ alert: boolean }> | null => {
  const { gatedOut, gatedIn } = container;

  const gatedOutDate = gatedOut?.occurred
    ? DateTime.fromFormat(gatedOut.occurred, DATE_FORMATS.BE_DATE_FORMAT, {
        zone: gatedOut.timezone,
      })
    : null;

  if (gatedOutDate) {
    const gatedInDate = gatedIn?.occurred
      ? DateTime.fromFormat(gatedIn.occurred, DATE_FORMATS.BE_DATE_FORMAT, {
          zone: gatedIn.timezone,
        })
      : DateTime.now();

    const interval = Interval.fromDateTimes(gatedOutDate, gatedInDate);
    const days = Math.floor(Math.max(1, interval.length('days')));

    if (days > 0) {
      return {
        value: `${days} ${pluralize('day', days)}`,
        meta: { alert: days > DETENTION_ALERT_DAYS_OUT },
      };
    }
  }

  return null;
};

export const formatMotherVessel = (container: Container): FormattedValue | null => {
  const { motherVessel } = container;

  if (!motherVessel) {
    return null;
  }

  return { value: motherVessel };
};

export const formatPortOfLoading = (container: Container): FormattedValue | null => {
  const { loadingLocation } = container;
  const value = loadingLocation?.port?.name || loadingLocation?.portRaw;
  if (!value) {
    return null;
  }

  return { value };
};

export const formatTerminalOfLoading = (container: Container): FormattedValue | null => {
  const { loadingLocation } = container;
  const value = loadingLocation?.terminal?.name || loadingLocation?.terminalRaw;
  if (!value) {
    return null;
  }

  return { value };
};

export const getPortCollection = (container: Container): string | null => {
  const { pickupLocation, returnLocation } = container;
  return (
    pickupLocation?.port?.portCollection?.code || returnLocation?.port?.portCollection?.code || null
  );
};

export const shouldShowPerDiem = (container: Container) => {
  return (
    container.currentStatus === CurrentContainerStatus.GatedOut ||
    container.currentStatus === CurrentContainerStatus.Terminated
  );
};
